/*
 * Copyright Red Hat, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import Tooltip from '@mui/material/Tooltip';
import { ChatbotDisplayMode } from '@patternfly/chatbot';

import { LightspeedFABIcon } from '../components/LightspeedIcon';
import { DOCKED_CONTENT_OFFSET } from '../const';
import { useLightspeedDrawerContext } from '../hooks/useLightspeedDrawerContext';
import { useTranslation } from '../hooks/useTranslation';

/**
 * @alpha
 * Lightspeed Floating action button to open/close the lightspeed chatbot
 */
export const LightspeedFAB = () => {
  const { t } = useTranslation();
  const { isChatbotActive, toggleChatbot, displayMode } =
    useLightspeedDrawerContext();

  if (displayMode === ChatbotDisplayMode.embedded) {
    return null;
  }

  return (
    <Box
      sx={theme => ({
        bottom: `calc(${theme.spacing(2)} + 1.5em)`,
        right: `calc(${theme.spacing(2)} + 1.5em)`,
        alignItems: 'end',
        zIndex: 200,
        display: 'flex',
        position: 'fixed',
        'body.docked-drawer-open &': {
          transition: 'margin-right 0.3s ease',
          marginRight: DOCKED_CONTENT_OFFSET,
        },
      })}
      id="lightspeed-fab"
      data-testid="lightspeed-fab"
    >
      <Tooltip
        title={isChatbotActive ? t('tooltip.fab.close') : t('tooltip.fab.open')}
        placement="left"
      >
        <Fab
          color="primary"
          variant="circular"
          size="small"
          onClick={toggleChatbot}
          aria-label={
            isChatbotActive ? t('tooltip.fab.close') : t('tooltip.fab.open')
          }
          sx={theme => ({
            borderRadius: '100% !important',
            opacity: '1 !important',
            backgroundColor: `${theme.palette?.primary?.main ?? '#1976d2'} !important`,
            color: `${theme.palette?.primary?.contrastText ?? '#fff'} !important`,
            boxShadow: `${
              theme.shadows?.[6] ??
              '0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 1px 18px 0px rgba(0,0,0,0.12)'
            } !important`,
          })}
        >
          {isChatbotActive ? <Close fontSize="small" /> : <LightspeedFABIcon />}
        </Fab>
      </Tooltip>
    </Box>
  );
};
